import React, {useEffect, useState} from 'react';
import {Box, Button, Link, Paper, Typography} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
import {isIosDevice} from '../../shared/misc/dom-utils';
import {iosAppStoreUrl, iosMeasureTreeUrl, linkId} from '../../shared/ios-app';

export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.savaquan.app';

/** How long the page waits for the app to take over before it offers the App Store. */
export const APP_OPEN_WAIT_MS = 2500;

// Take Measured Picture's address. A phone app with the link claimed opens
// before the browser gets here (Android's App Links, iOS's universal links
// from another site), so reaching this page means no app took it. Safari
// never hands a link to the page's own domain to an app, so on an iPhone the
// web's own button always lands here: the page tries the app on its URL
// scheme and, when the page is still in front after a moment, goes to the
// App Store page (or says where to get the app while it has none). Anything
// else goes to Google Play, as before.
const MeasureTreePage = () => {
  const [params] = useSearchParams();
  const estimateId = linkId(params.get('estimate-id'));
  const treeId = linkId(params.get('tree-id'));
  const [ios] = useState(isIosDevice);
  const appUrl = ios ? iosMeasureTreeUrl(estimateId, treeId) : undefined;
  const storeUrl = iosAppStoreUrl();
  const [noApp, setNoApp] = useState(ios && !appUrl);

  useEffect(() => {
    if (!ios) {
      window.location.replace(GOOGLE_PLAY_URL);
      return;
    }
    // The iPhone app signs in on its own: the token Android uses has no place in this tab's history.
    if (params.has('token')) {
      const kept = new URLSearchParams(params);
      kept.delete('token');
      window.history.replaceState(window.history.state, '', `${window.location.pathname}?${kept.toString()}`);
    }
    if (!appUrl) {
      return;
    }
    // The app opening sends Safari to the background; so does its "Open in …?" question.
    let left = false;
    const away = () => { left = true; };
    const hidden = () => { if (document.visibilityState === 'hidden') left = true; };
    window.addEventListener('pagehide', away);
    window.addEventListener('blur', away);
    document.addEventListener('visibilitychange', hidden);
    window.location.href = appUrl;
    const timer = window.setTimeout(() => {
      if (left) {
        return;
      }
      if (storeUrl) {
        window.location.replace(storeUrl);
      } else {
        setNoApp(true);
      }
    }, APP_OPEN_WAIT_MS);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pagehide', away);
      window.removeEventListener('blur', away);
      document.removeEventListener('visibilitychange', hidden);
    };
    // Once per visit: the address does not change under this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ios) {
    return <Typography sx={{p: 2}}>Redirecting…</Typography>;
  }
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
      <Paper elevation={3} sx={{p: 4, width: '100%', maxWidth: 400}}>
        <Typography variant="h5" align="center" gutterBottom>
          SavaQuan
        </Typography>
        <Typography align="center" sx={{mb: 2}}>
          {noApp ? 'Take Measured Picture needs the SavaQuan iPhone app.' : 'Opening the SavaQuan app to measure the tree…'}
        </Typography>
        {appUrl && <Button variant="contained" fullWidth href={appUrl} sx={{mb: 1.5}}>Open the app</Button>}
        {storeUrl ? (
          <Button variant="outlined" fullWidth href={storeUrl}>Get the app on the App Store</Button>
        ) : noApp && (
          <Typography variant="body2" color="text.secondary" align="center">
            Install the SavaQuan app on this iPhone, then tap Take Measured Picture again.
          </Typography>
        )}
        {estimateId && treeId && (
          <Typography variant="body2" align="center" sx={{mt: 2}}>
            <Link href={`/estimates/${estimateId}/itemized/tree/${treeId}`}>Back to the estimate</Link>
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default MeasureTreePage;
